import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/auth.css';

const EmailVerification = () => {
  const { token } = useParams();
  const { verifyEmail, resendVerificationEmail, user, isEmailVerified } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const navigate = useNavigate();

  // Verificar el token si está presente en la URL
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        await verifyEmail(token);
        setSuccess(true);
        setError(null);
      } catch (error) {
        console.error('Error al verificar el email:', error);
        setError(
          error.message ||
            'Error al verificar el email. Por favor, solicita un nuevo enlace de verificación.',
        );
        setSuccess(false);
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token, verifyEmail]);

  // Redirigir si el usuario ya está verificado
  useEffect(() => {
    if (isEmailVerified && user) {
      navigate('/dashboard');
    }
  }, [isEmailVerified, user, navigate]);

  // Manejar el reenvío del email de verificación
  const handleResendVerification = async (e) => {
    e.preventDefault();
    setResendLoading(true);
    setError(null);
    setResendSuccess(false);

    try {
      await resendVerificationEmail(email || (user && user.email));
      setResendSuccess(true);
    } catch (error) {
      console.error('Error al reenviar el email de verificación:', error);
      setError(
        error.message ||
          'Error al reenviar el email de verificación. Por favor, inténtalo de nuevo más tarde.',
      );
    } finally {
      setResendLoading(false);
    }
  };

  // Renderizar pantalla de carga
  if (loading) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h2>Verificando Email</h2>
          </div>
          <div className="auth-body">
            <div className="loading-spinner"></div>
            <p className="text-center">Verificando tu email...</p>
          </div>
        </div>
      </div>
    );
  }

  // Renderizar pantalla de éxito
  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h2>Email Verificado</h2>
          </div>
          <div className="auth-body">
            <div className="success-icon">
              <span className="material-icons">check_circle</span>
            </div>
            <p className="text-center">¡Tu email ha sido verificado correctamente!</p>
            <div className="auth-actions">
              <Link to="/dashboard" className="btn btn-primary btn-block">
                Ir al Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Renderizar formulario de reenvío
  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h2>Verificación de Email</h2>
        </div>
        <div className="auth-body">
          {error && <div className="alert alert-error">{error}</div>}
          {resendSuccess && (
            <div className="alert alert-success">
              Se ha enviado un nuevo enlace de verificación a tu email. Por favor, revisa tu bandeja
              de entrada.
            </div>
          )}

          <p className="text-center">
            {token
              ? 'El enlace de verificación ha expirado o no es válido. Por favor, solicita un nuevo enlace de verificación.'
              : 'Para acceder a todas las funcionalidades, necesitas verificar tu email. Por favor, revisa tu bandeja de entrada o solicita un nuevo enlace de verificación.'}
          </p>

          <form onSubmit={handleResendVerification}>
            {!user && (
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Ingresa tu email"
                  required
                />
              </div>
            )}

            <div className="auth-actions">
              <button type="submit" className="btn btn-primary btn-block" disabled={resendLoading}>
                {resendLoading ? 'Enviando...' : 'Reenviar Email de Verificación'}
              </button>
              <Link to="/login" className="btn btn-link btn-block">
                Volver al Inicio de Sesión
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
